import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uniqBy } from 'lodash';
import type { AnchorHTMLAttributes, Dispatch, FC, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import type {
  NetworkPoint,
  NetworkPointsParams,
} from 'src/apis/network-points/types';
import type { NetworkPointsRequest } from 'src/apis/network-points/use-network-points';
import type { NetworkPointsRootRequest } from 'src/apis/network-points/use-network-points-root';
import { ActionGroup } from 'src/components/buttons-and-actions/action-group';
import { Button } from 'src/components/buttons-and-actions/button';
import { Direction } from 'src/components/data-display/direction';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { MetaLabel } from 'src/components/data-display/meta-label';
import { Table } from 'src/components/data-display/smart-table';
import type { TrProps } from 'src/components/data-display/table';
import { Td, Th, Tr } from 'src/components/data-display/table';
import { SimpleCheckbox } from 'src/components/form/checkbox';
import type { FormModeContextValue } from 'src/components/form/form-mode-context';
import { SearchableSingleSelectItem } from 'src/components/form/select/searchable-single-select';
import { Link } from 'src/components/navigation/link';
import { Spinner, SpinnerContainer } from 'src/components/spinner-container';
import { Hint } from 'src/components/text/hint';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { handleFakeOpenInBackgroundTab } from 'src/utils/open-in-background-tab';

export type NetworkPointPopoverParams = {
  q: string | null;
  favouritesAboveIfInitialSearch: boolean;
  case: 'SHOW_ALL' | 'ACTIVE_ONLY' | 'ONLY_FAVORITE' | 'ONLY_OWN';
  limit: number;
  offset: number;
};

type NetworkPointPopoverProps = {
  internalParams: NetworkPointPopoverParams;
  setInternalParams: Dispatch<SetStateAction<NetworkPointPopoverParams>>;
  params: NetworkPointsParams;
  networkPointsRoot: NetworkPointsRootRequest;
  networkPoints: NetworkPointsRequest;
  loadMoreNetworkPoints: NetworkPointsRequest;
  formMode: FormModeContextValue;
  pending: boolean;
  items: NetworkPoint[] | undefined;
  name: string;
  searchValueForCurrentOptions: string | null | undefined;
} & (
  | {
      type: 'single';
      selectedId: string | undefined;
      selectedIds?: undefined;
    }
  | {
      type: 'multi';
      selectedId?: undefined;
      selectedIds: string[];
    }
);

export const NetworkPointPopover: FC<NetworkPointPopoverProps> = ({
  name,
  internalParams,
  setInternalParams,
  params,
  networkPointsRoot,
  networkPoints,
  loadMoreNetworkPoints,
  formMode,
  pending,
  items,
  searchValueForCurrentOptions,
  type,
  selectedId,
  selectedIds,
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  // merge first network point request with "load more" results
  useEffect(() => {
    if (!networkPoints.response) return;
    if (!loadMoreNetworkPoints.response) return;

    const items = uniqBy(
      [
        ...networkPoints.response.data._embedded.items,
        ...loadMoreNetworkPoints.response.data._embedded.items,
      ],
      'id'
    );

    networkPoints.setResponse({
      ...networkPoints.response,
      data: {
        ...networkPoints.response.data,
        _embedded: { items },
        limit:
          loadMoreNetworkPoints.response.data.offset +
          loadMoreNetworkPoints.response.data.limit,
      },
    });
    // oxlint-disable-next-line exhaustive-deps
  }, [loadMoreNetworkPoints.response]);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // whenever params change we scroll back to the top
  useEffect(() => {
    if (!tableWrapperRef.current) return;
    tableWrapperRef.current.scrollTop = 0;
  }, [internalParams]);

  const linkProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
    target: formMode === 'search' ? undefined : '_blank',
    onClick: (e) => e.stopPropagation(),
  };

  return (
    <>
      {(monolithUser?.hasShipper || monolithUser?.hasTso) && (
        <QuickFilter
          hasTsoRole={monolithUser.hasTso}
          hasShipperRole={monolithUser.hasShipper}
          internalParams={internalParams}
          setInternalParams={setInternalParams}
        />
      )}

      <SpinnerContainer
        pending={pending}
        data-testid="nwp-search-spinner-container"
      >
        <div
          ref={tableWrapperRef}
          className={`
            max-h-[min(var(--popover-available-height),50rem)] overflow-auto
          `}
          data-testid="nwp-search-results-scroll-container"
          onScroll={(event) => {
            // when we scroll to the bottom we try to load more network points
            if (!networkPointsRoot.response) return;
            if (!networkPoints.response) return;
            if (networkPoints.pending) return;
            if (loadMoreNetworkPoints.pending) return;

            const element = event.currentTarget;
            const offset = 100;
            const scrollPos = element.scrollHeight - element.scrollTop - offset;
            const reachedBottom = scrollPos < element.clientHeight;

            const data =
              loadMoreNetworkPoints.response?.data ??
              networkPoints.response.data;
            const moreToLoad = data.offset + data.limit < data.total;

            if (!reachedBottom || !moreToLoad) return;

            loadMoreNetworkPoints.execute({
              networkPointsRoot,
              params: { ...params, offset: data.offset + data.limit },
            });
          }}
        >
          <Table
            data-testid="nwp-search-table"
            respectHeader={false}
            data={items ?? []}
            setId={(item) => String(item.id)}
            body={({ tbodyProps }) => <tbody {...tbodyProps} />}
            // table renders _options_ that belong to a form field,
            // but it doesn't contain any form fields itself
            containsFormFields={false}
            row={(props) => (
              <SelectRow
                {...props}
                name={name}
                mode={formMode}
                selected={
                  type === 'single'
                    ? props.item.id === selectedId
                    : selectedIds.includes(props.item.id)
                }
              />
            )}
            cols={[
              type === 'multi' && {
                key: 'checkbox',
                width: '1.5rem',
                head: <Th />,
                body: (item) => (
                  <Td>
                    <SimpleCheckbox
                      label={item.name}
                      hideLabel
                      checked={selectedIds.includes(item.id)}
                      onChange={(event) =>
                        event.currentTarget.parentElement?.click()
                      }
                    />
                  </Td>
                ),
              },
              {
                key: 'networkPoint',
                width: 3,
                head: <Th>Network Point</Th>,
                body: (item) => (
                  <Td>
                    {monolithUser?.hasShipper && item.favourite && (
                      <>
                        <FontAwesomeIcon icon={faStar} />{' '}
                      </>
                    )}
                    {item.name}
                    <br />
                    <Direction networkPoint={item} />
                    {!item.active && (
                      <>
                        {' '}
                        <MetaLabel color="inactive">Inactive</MetaLabel>
                      </>
                    )}
                  </Td>
                ),
              },
              {
                key: 'marketer',
                width: 2,
                head: <Th>TSO</Th>,
                body: (item) => (
                  <Td>
                    {item.bundle ? (
                      <>
                        <Link
                          mode="default-underlined"
                          to={`/market-information/players/operators/${item.exit.operatorId}`}
                          {...linkProps}
                        >
                          {item.exit.operatorShortName}
                        </Link>{' '}
                        /{' '}
                        <Link
                          mode="default-underlined"
                          to={`/market-information/players/operators/${item.entry.operatorId}`}
                          {...linkProps}
                        >
                          {item.entry.operatorShortName}
                        </Link>
                      </>
                    ) : (
                      <Link
                        mode="default-underlined"
                        to={`/market-information/players/operators/${item.operatorId}`}
                        {...linkProps}
                      >
                        {item.operatorShortName}
                      </Link>
                    )}
                  </Td>
                ),
              },
              {
                key: 'id',
                width: 3,
                head: <Th>Network Point ID</Th>,
                body: (item) => (
                  <Td>
                    <p>{item.identifier}</p>

                    {showEic(item) ? (
                      <Hint size="sm">EIC: {item.eic}</Hint>
                    ) : (
                      <small>
                        <EmptyValue />
                      </small>
                    )}
                  </Td>
                ),
              },
            ]}
            empty={{
              title: 'No Matching Network Points',
              description:
                items && items.length === 0 && searchValueForCurrentOptions
                  ? `Could not find network points for "${searchValueForCurrentOptions}".`
                  : 'Could not find network points.',
            }}
          />
          {loadMoreNetworkPoints.pending && (
            <div
              className="m-xl grid items-center justify-center"
              data-testid="nwp-search-loading-more"
            >
              <Spinner />
            </div>
          )}
        </div>
      </SpinnerContainer>
    </>
  );
};

function showEic(networkPoint: NetworkPoint) {
  if (!networkPoint.eic) return false;
  if (networkPoint.eic === networkPoint.name) return false;
  if (networkPoint.eic === networkPoint.identifier) return false;
  if (networkPoint.eic === 'not applicable') return false;
  if (networkPoint.eic === 'n/a') return false;
  return true;
}

const SelectRow: FC<{
  item: NetworkPoint;
  name: string;
  trProps: TrProps;
  mode: FormModeContextValue;
  selected: boolean;
}> = ({ item, name, trProps, mode, selected }) => {
  return (
    <SearchableSingleSelectItem
      value={item.id}
      data-testid={`${name}-option-${item.id}`}
      // it looks like for a real combobox `aria-selected` is not recommended,
      // therefor ariakit doesn't set it. we use our own attribute here
      // for testin purposes only.
      data-custom-is-selected={selected}
      render={(itemProps) => (
        <Tr
          {...trProps}
          {...itemProps}
          onClick={(event) => {
            if (event.target instanceof HTMLAnchorElement) return;

            // The `<tr/>` is supposed to act as a link for _some cases_ (opening background tabs),
            // but we cannot use a real `<a/>` as it would create invalid markup.
            // (A `<tr/>` cannot be a child or parent of an `<a/>`.)
            handleFakeOpenInBackgroundTab(
              event,
              `${location.origin}/network-points-redirect/${item.id}`
            );
            if (event.isPropagationStopped()) return;

            itemProps.onClick?.(event);
          }}
          // within search mode we don't visualize the selection
          isSelected={mode === 'search' ? false : selected}
        >
          {trProps.children}
        </Tr>
      )}
    />
  );
};

const QuickFilter: FC<{
  hasTsoRole: boolean | undefined;
  internalParams: NetworkPointPopoverParams;
  setInternalParams: Dispatch<SetStateAction<NetworkPointPopoverParams>>;
  hasShipperRole: boolean | undefined;
}> = ({ hasTsoRole, internalParams, setInternalParams, hasShipperRole }) => {
  return (
    <div className="grid items-center justify-items-end">
      <div className="m-md">
        <ActionGroup mode="segmented-control">
          {hasTsoRole && (
            <Button
              isSelected={internalParams.case === 'SHOW_ALL'}
              onClick={() =>
                setInternalParams((params) => ({
                  ...params,
                  case: 'SHOW_ALL',
                }))
              }
              data-testid="nwp-search-show-all"
            >
              Show All
            </Button>
          )}

          <Button
            isSelected={internalParams.case === 'ACTIVE_ONLY'}
            onClick={() =>
              setInternalParams((params) => ({
                ...params,
                case: 'ACTIVE_ONLY',
              }))
            }
            data-testid="nwp-search-active-only"
          >
            {hasShipperRole && !hasTsoRole ? 'Show All' : 'Active Only'}
          </Button>

          {hasShipperRole && (
            <Button
              isSelected={internalParams.case === 'ONLY_FAVORITE'}
              onClick={() =>
                setInternalParams((params) => ({
                  ...params,
                  case: 'ONLY_FAVORITE',
                }))
              }
              data-testid="nwp-search-favorites-only"
            >
              <FontAwesomeIcon icon={faStar} /> Favourites Only
            </Button>
          )}

          {hasTsoRole && (
            <Button
              isSelected={internalParams.case === 'ONLY_OWN'}
              onClick={() =>
                setInternalParams((params) => ({
                  ...params,
                  case: 'ONLY_OWN',
                }))
              }
              data-testid="nwp-search-own-only"
            >
              Own Only
            </Button>
          )}
        </ActionGroup>
      </div>
    </div>
  );
};
